import React, { ReactElement, useEffect, useState, useCallback } from 'react';
import {
  Button as MUIButton,
  Divider,
  FormControl,
  IconButton,
  Paper,
  RadioGroup,
  Skeleton,
  Stack,
  Table,
  TableContainer,
  Typography,
} from '@mui/material';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import api from '../../services/api';
import { servicePackage } from '../../schema/servicePackage';
import { toaster } from '../../services/toaster';
import Tooltip from '@mui/material/Tooltip';
import AddIcon from '@mui/icons-material/Add';
import CalculateIcon from '@mui/icons-material/Calculate';
import TextFieldMUI from '@mui/material/TextField';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogActions from '@mui/material/DialogActions';
import { debounce } from 'lodash';
import { SchemaWithClient } from '../../types/SchemaWithClient';
import { Entity } from '../../types/entities/Entity';
import { SlidingPaneEdit } from '../Page/SlidingPaneEdit';
import CloseIcon from '@mui/icons-material/Close';
import { formatNumber } from '../../reducer/actions/formatNumber';
import RefreshIcon from '@mui/icons-material/Refresh';
import { SlidingPaneAddNew } from '../Page/SlidingPaneAddNew';
import CloudDoneRoundedIcon from '@mui/icons-material/CloudDoneRounded';
import Fade from '@mui/material/Fade';
import CircularProgress from '@mui/material/CircularProgress';
import TableBody from '@mui/material/TableBody';
import DialogContent from '@mui/material/DialogContent';
import { SPNumberField } from '../Table/fields/SPNumberField';
import Box from '@mui/material/Box';
import ServiceGroupRow from '../Activity/Services/ServiceGroupRow';
import toast from 'react-hot-toast';
import {useStateContext} from "../../hooks/useStateContext";
import {hasPermissionByPriority} from "../../core/permissions/hasPermission";
import {ROLE_PRIORITIES} from "../../core/permissions/permissions";

type Props<T extends Entity> = {
  schema: SchemaWithClient<T>;
};

export const Services = ({
  schema,
  values,
}: {
  schema: any;
  values: any;
}): ReactElement => {
  const [openedSlidingPane, setOpenedSlidingPane] = useState<any>({});
  const [currentAddNewPaneEntity, setCurrentAddNewPaneEntity] = React.useState<
    any
  >({
    schema: schema,
    row: {},
    key: undefined,
  });
  const [openViewPrices, setOpenViewPrices] = useState<any>(undefined);
  const [openViewPriceColumns, setOpenViewPriceColumns] = useState<any>({});
  const [openConfirmDialog, setOpenConfirmDialog] = useState<any>(false);
  const [calculatorMode, setCalculatorMode] = useState<boolean>(false);
  const [properties, setProperties] = useState<any>(undefined);
  const [propertiesForCalculate, setPropertiesForCalculate] = useState<any>(
    undefined
  );

  const [query, setQuery] = React.useState<'idle' | 'progress' | 'success'>(
    'idle'
  );
  const timerRef = React.useRef<ReturnType<typeof setTimeout>>();

  const [guideWage, setGuideWage] = useState<number>(1000);
  const [guideHoursSum, setGuideHoursSum] = useState<number>(2000);
  const [eurCurrentValue, setEurCurrentValue] = useState<number>(0);
  const [groupSize, setGroupSize] = useState<number>(10);
  const [calculatedSum, setCalculatedSum] = useState<any>(undefined);

  const [loadingExchange, setLoadingExchange] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);

  const debouncedOnChangeCalculator = debounce(
    handleDebouncedOnChangeCalculator,
    0
  );

  function handleDebouncedOnChangeCalculator(value: any, type: string) {
    type === 'group_size'
      ? setGroupSize(parseInt(value))
      : setEurCurrentValue(value);
  }

  const handleSetPricingService = (servicePackage: any) => {
    if (!calculatorMode) {
      api.patch(`/activities/${values.id}`, {
        service_package: {
          id: servicePackage,
        },
      });
    }

    const oldProperties = calculatorMode
      ? {
          ...propertiesForCalculate,
          ['service_package_id']: parseInt(servicePackage),
        }
      : { ...properties, ['service_package_id']: parseInt(servicePackage) };

    if (calculatorMode) {
      setPropertiesForCalculate(oldProperties);
    } else {
      setProperties(oldProperties);
    }
  };

  const handleSetExtraService = (service: any, value: boolean) => {
    setQuery('progress');

    api
      .patch(`/service-packages/${service?.id}`, {
        extra: value,
      })
      .finally(() => {
        timerRef.current = setTimeout(() => {
          setQuery('success');
        }, 2000);
      });

    const oldProperties = calculatorMode
      ? { ...propertiesForCalculate }
      : { ...properties };
    const servicePackages = oldProperties[`services`]
      ? oldProperties[`services`]
      : [];

    const newRows = servicePackages.map((servicePackage: any) => {
      if (servicePackage?.id === service?.id) {
        servicePackage.extra = value;
      }
      return servicePackage;
    });

    if (calculatorMode) {
      setPropertiesForCalculate({
        ...propertiesForCalculate,
        ['services']: newRows,
      });
    } else {
      setProperties({ ...properties, ['services']: newRows });
    }
  };

  const handleDeleteServicePackage = useCallback(
    async (deletedRow: any) => {
      api
        .delete(`/service-packages/${deletedRow?.id}`)
        .then((res: any) => {
          fetchServices();
          toast.success(
            `Successfully deleted Service: ${
              deletedRow?.name ? deletedRow?.name : deletedRow?.id
            }`
          );
          setOpenConfirmDialog(false);
        })
        .catch((error: any) => {
          let array: any = [];
          for (let errorKey in error) {
            for (let errorMsg in error[errorKey]) {
              array.push(`${error[errorKey][errorMsg]}`);
            }
          }
          toast.error(array.join(','));
        });
    },
    [properties]
  );

  const fetchServices = useCallback(() => {
    setLoading(true);
    const searchParams = new URLSearchParams({
      activity: values?.id,
    });

    api.get('/activity-services', searchParams).then((res: any) => {
      setProperties(res);
      setLoading(false);
    });
  }, []);
  const fetchGuideWage = useCallback(() => {
    const wage = api.get('/guide-wages/current').then((res: any) => {
      if (res?.value) {
        setGuideWage(res.value);
      }
    });
  }, []);

  const handleDeleteServiceInPackage = useCallback(
    async (deletedRow: any) => {
      api
        .delete(`/service-packages/service/${deletedRow?.id}`)
        .then((res: any) => {
          fetchServices();
          toast.success(
            `Successfully deleted Service Group: ${
              deletedRow?.name ? deletedRow?.name : deletedRow?.id
            }`
          );
          setOpenConfirmDialog(false);
        })
        .catch((error: any) => {
          let array: any = [];
          for (let errorKey in error) {
            for (let errorMsg in error[errorKey]) {
              array.push(`${error[errorKey][errorMsg]}`);
            }
          }
          toast.error(array.join(','));
        });
    },
    [properties]
  );

  const fetchExchangeRate = useCallback(async () => {
    setLoadingExchange(true);

    const response = await fetch(
      `https://api.exchangerate-api.com/v4/latest/EUR`
    );
    const data = await response.json();
    setLoadingExchange(false);
    setEurCurrentValue(data.rates.HUF);
  }, []);

  useEffect(() => {
    if (propertiesForCalculate) {
      const summaries: any = [];

      propertiesForCalculate?.services.map((servicePackage: any) => {
        let servicePackageCostSumNet = 0;
        let servicePackageCostSumGross = 0;

        servicePackage?.services.map((service: any, index: number) => {
          // FIX / FULL
          if (service?.service_type_id === 2) {
            if (service?.currency) {
              let fullPrice = service?.unit_price ? service?.unit_price : 0;
              let fullPriceGross = service?.unit_price_gross
                ? service?.unit_price_gross
                : 0;
              if (service.currency.id === 1) {
                fullPrice = parseFloat(
                  (fullPrice * eurCurrentValue).toFixed(0)
                );
                fullPriceGross = parseFloat(
                  (fullPriceGross * eurCurrentValue).toFixed(0)
                );
              }

              servicePackageCostSumNet += fullPrice;
              servicePackageCostSumGross += fullPriceGross;
            }
          }
          // PP
          if (service?.service_type_id === 1) {
            if (service?.currency) {
              let ppPrice =
                (service?.unit_price ? service?.unit_price : 0) * groupSize;
              let ppPriceGross =
                (service?.unit_price_gross ? service?.unit_price_gross : 0) *
                groupSize;
              if (service.currency.id === 1) {
                ppPrice = parseFloat((ppPrice * eurCurrentValue).toFixed(0));
                ppPriceGross = parseFloat(
                  (ppPriceGross * eurCurrentValue).toFixed(0)
                );
              }

              servicePackageCostSumNet += ppPrice;
              servicePackageCostSumGross += ppPriceGross;
            }
          }
          // VARIABLE
          if (service?.service_type_id === 3) {
            for (let i = 1; i < 51; i++) {
              if (groupSize == i) {
                if (service?.currency) {
                  let variablePrice = service[`size_${i}`];

                  let variablePriceGross = parseFloat(
                    (
                      service[`size_${i}`] *
                      (parseFloat((service['vat'] / 100).toFixed(2)) + 1)
                    ).toFixed(2)
                  );

                  if (service.currency.id === 1) {
                    variablePrice = parseFloat(
                      (variablePrice * eurCurrentValue).toFixed(2)
                    );
                    variablePriceGross = parseFloat(
                      (variablePriceGross * eurCurrentValue).toFixed(2)
                    );
                  }
                  servicePackageCostSumNet += variablePrice;
                  servicePackageCostSumGross += variablePriceGross;
                }
              }
            }
          }
        });

        if (servicePackage?.guide_hour) {
          servicePackageCostSumNet += servicePackage.guide_hour;
          servicePackageCostSumGross += servicePackage.guide_hour;
        }

        summaries.push({
          net: servicePackageCostSumNet,
          gross: servicePackageCostSumGross,
          base: servicePackage.base,
          data: servicePackage,
        });

        return false;
      });

      let basePackage: any = {};

      const options = summaries.filter((servicePackage: any) => {
        if (servicePackage.base) {
          basePackage = servicePackage;
          return false;
        } else {
          return true;
        }
      });

      const summaryTable: any = [];

      options.map((servicePackage: any) => {
        const constNet = parseInt(
          servicePackage.net + (basePackage?.net ? basePackage.net : 0)
        );
        const constGross = parseInt(
          servicePackage.gross + (basePackage?.gross ? basePackage.gross : 0)
        );

        summaryTable.push({
          name: servicePackage.data?.name,
          net: {
            total_huf: formatNumber(constNet),
            pp_huf: formatNumber(parseInt((constNet / groupSize).toFixed(0))),
            total_eur: formatNumber((constNet / eurCurrentValue).toFixed(2)),
            pp_eur: formatNumber(
              (constNet / eurCurrentValue / groupSize).toFixed(2)
            ),
          },
          gross: {
            total_huf: formatNumber(constGross),
            pp_huf: formatNumber(parseInt((constGross / groupSize).toFixed(0))),
            total_eur: formatNumber((constGross / eurCurrentValue).toFixed(2)),
            pp_eur: formatNumber(
              (constGross / eurCurrentValue / groupSize).toFixed(2)
            ),
          },
        });
      });

      setCalculatedSum(summaryTable);
    }
  }, [
    propertiesForCalculate,
    guideWage,
    eurCurrentValue,
    groupSize,
    calculatorMode,
  ]);

  useEffect(() => {
    setPropertiesForCalculate(properties);
  }, [properties]);

  useEffect(() => {
    let sum = 0;
    if (properties) {
      properties.services.map((servicePackage: any) => {
        //console.log(servicePackage);
        //sum += item?.value;
      });
      setGuideHoursSum(sum);
    }

    fetchExchangeRate();
    fetchGuideWage();
  }, []);

  useEffect(() => {
    fetchServices();

    const columns: any = {};
    for (let i = 1; i < 51; i++) {
      columns[`size_${i}`] = i;
    }

    setOpenViewPriceColumns(columns);
  }, []);

  const debouncedOnChange = debounce(handleDebouncedOnChange, 200);

  const moveServicePackageRow = (fromIndex: any, toIndex: any) => {
    debouncedOnChange(fromIndex, toIndex);
  };

  function handleDebouncedOnChange(fromIndex: any, toIndex: any) {
    const updatedRows = [...properties.services];
    const [movedRow] = updatedRows.splice(fromIndex, 1);
    updatedRows.splice(toIndex, 0, movedRow);
    setProperties({ ...properties, services: updatedRows });

    updatedRows.map((item, index) => {
      api.patch(`/service-packages/${item?.id}`, {
        position: index,
      });
    });
  }

  const {user} = useStateContext();
  const isAdmin = hasPermissionByPriority(user.roles, ROLE_PRIORITIES[1]);

  return (
    <>
      {calculatorMode && (
        <Stack
          direction={'row'}
          gap={1}
          justifyContent={'space-between'}
          alignItems={'center'}
          marginBottom={'20px'}
          marginTop={'20px'}
          sx={{
            background: '#d5d5d5',
            padding: '10px',
            width: '100%',
          }}
        >
          <Stack direction={'column'}>
            <Typography variant="h6" component="div">
              CALCULATOR MODE
            </Typography>
            <Typography variant="inherit" component="div">
              changes in the services list will not affect activity defaults
            </Typography>
          </Stack>

          <Stack direction={'row'} gap={1} alignItems={'center'}>
            <Typography variant="subtitle1" component="div">
              EUR/HUF
            </Typography>
            <TextFieldMUI
              disabled={loadingExchange}
              variant="outlined"
              size={'small'}
              type={'number'}
              value={eurCurrentValue}
              sx={{
                '& input': {
                  textAlign: 'right',
                  width: '100px',
                },
                '& .MuiInputBase-root.MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(0,0,0,0.2)',
                },
              }}
              onChange={({ target: { value } }) => {
                debouncedOnChangeCalculator(value, 'eur');
              }}
            />
            <IconButton
              disabled={loadingExchange}
              color={'primary'}
              size={'small'}
              onClick={() => {
                fetchExchangeRate();
              }}
            >
              <RefreshIcon />
            </IconButton>
          </Stack>

          <Stack direction={'row'} alignItems={'center'} gap={1}>
            <Typography variant="subtitle2" component="div">
              Group Size
            </Typography>
            <TextFieldMUI
              variant="outlined"
              size={'small'}
              type={'number'}
              defaultValue={10}
              sx={{
                '& input': {
                  textAlign: 'right',
                  width: '50px',
                },
                '& .MuiInputBase-root.MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(0,0,0,0.2)',
                },
              }}
              onChange={({ target: { value } }) => {
                debouncedOnChangeCalculator(value, 'group_size');
              }}
            />
          </Stack>

          <IconButton
            color={'inherit'}
            onClick={() => {
              setCalculatorMode(false);
            }}
          >
            <CloseIcon />
          </IconButton>
        </Stack>
      )}

      {loading && !properties ? (
        <>
          <Skeleton variant="rectangular" width={'100%'} height={30} />
          <Skeleton
            variant="rectangular"
            width={'100%'}
            height={100}
            sx={{
              marginTop: '10px',
            }}
          />
        </>
      ) : (
        <>
          <TableContainer component={Paper}>
            <FormControl
              sx={{
                display: 'block',
              }}
            >
              <RadioGroup
                aria-labelledby="demo-radio-buttons-group-label"
                name="radio-buttons-group"
                sx={{
                  '& .MuiRadio-root': {
                    padding: '2px',
                    width: '100%',
                  },
                }}
                onChange={(event, value) => {
                  handleSetPricingService(value);
                }}
                value={
                  calculatorMode
                    ? propertiesForCalculate?.service_package_id
                      ? propertiesForCalculate.service_package_id
                      : undefined
                    : properties?.service_package_id
                    ? properties.service_package_id
                    : undefined
                }
              >
                <Table aria-label="responsive table">
                  <TableHead
                    sx={{
                      '& .MuiTableCell-root': {
                        color: 'inherit',
                        border: 'none',
                      },
                      '& .MuiTableRow-root .MuiTableCell-root.MuiTableCell-head': {
                        border: 'none',
                        fontSize: '14px',
                        p: '10px',
                      },
                      background: '#256861',
                      color: '#fff',
                    }}
                  >
                    <TableRow>
                      <TableCell></TableCell>
                      <TableCell>Service Group</TableCell>
                      <TableCell>Default</TableCell>
                      <TableCell>Extra</TableCell>
                      <TableCell>Assigned Staff</TableCell>
                      <TableCell colSpan={10}></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(calculatorMode
                      ? propertiesForCalculate
                      : properties
                    ).services.map(
                      (mainRow: any, servicePackageIndex: number) => {
                        const servicesPLusRolesPlusHours =
                          mainRow.services.length;
                        return (
                          <ServiceGroupRow
                            calculatorMode={calculatorMode}
                            row={mainRow}
                            key={servicePackageIndex}
                            index={servicePackageIndex}
                            moveRow={moveServicePackageRow}
                            clickOnEdit={(value: any) => {
                              setOpenedSlidingPane(value);
                            }}
                            handleSetExtraService={(value: any) => {
                              handleSetExtraService(mainRow, value);
                            }}
                            clickOnOpenViewPrices={(value: any) => {
                              setOpenViewPrices(value);
                            }}
                            clickOnDeleteService={(deletedRow: any) => {
                              handleDeleteServiceInPackage(deletedRow);
                            }}
                            clickOnDelete={() => {
                              handleDeleteServicePackage(mainRow);
                            }}
                          />
                        );
                      }
                    )}
                  </TableBody>
                </Table>
              </RadioGroup>
            </FormControl>
          </TableContainer>
        </>
      )}

      <Stack
        direction={'row'}
        alignItems={'center'}
        sx={{
          width: '100%',
        }}
        justifyContent={'space-between'}
      >
        <Stack direction={'row'}>
          <Tooltip
            title={`Add new Service Group`}
            slotProps={{
              popper: {
                modifiers: [
                  {
                    name: 'offset',
                    options: {
                      offset: [0, -14],
                    },
                  },
                ],
              },
            }}
          >
            <IconButton
              disabled={!isAdmin}
              color={'primary'}
              onClick={() => {
                setCurrentAddNewPaneEntity({
                  row: {
                    id: 'new',
                    activity: {
                      id: values?.id,
                      name: values?.name,
                    },
                  },
                  schema: servicePackage(),
                  key: 'services',
                });
              }}
            >
              <AddIcon />
            </IconButton>
          </Tooltip>

          {!calculatorMode && (
            <Tooltip
              title={`Calculator mode`}
              slotProps={{
                popper: {
                  modifiers: [
                    {
                      name: 'offset',
                      options: {
                        offset: [0, -14],
                      },
                    },
                  ],
                },
              }}
            >
              <IconButton
                disabled={!isAdmin}
                color={'primary'}
                onClick={() => {
                  setCalculatorMode(true);
                }}
              >
                <CalculateIcon />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
        <Stack>
          {query === 'success' ? (
            <Stack direction={'row'} alignItems={'center'}>
              <CloudDoneRoundedIcon color={'disabled'} />
              <Typography variant={'caption'} marginLeft={'5px'} color={'gray'}>
                successfully saved
              </Typography>
            </Stack>
          ) : (
            <Fade
              in={query === 'progress'}
              style={{
                transitionDelay: query === 'progress' ? '800ms' : '0ms',
              }}
              unmountOnExit
            >
              <CircularProgress size={20} />
            </Fade>
          )}
        </Stack>
      </Stack>

      <Divider />

      {calculatorMode && (
        <Box
          marginBottom={'20px'}
          marginTop={'20px'}
          sx={{
            background: '#d5d5d5',
            padding: '10px',
            width: '100%',
          }}
        >
          <Typography variant={'h6'}>Calculated data:</Typography>

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell></TableCell>
                  <TableCell colSpan={4} align={'center'} sx={{}}>
                    NET
                  </TableCell>
                  <TableCell
                    sx={{
                      background: '#f6f6f6',
                      fontWeight: 'bold',
                    }}
                    colSpan={4}
                    align={'center'}
                  >
                    GROSS
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Option</TableCell>

                  <TableCell align={'right'} sx={{}}>
                    Total HUF
                  </TableCell>
                  <TableCell align={'right'} sx={{}}>
                    Per Person HUF
                  </TableCell>
                  <TableCell align={'right'} sx={{}}>
                    Total EUR
                  </TableCell>
                  <TableCell align={'right'} sx={{}}>
                    Per Person EUR
                  </TableCell>

                  <TableCell
                    sx={{
                      background: '#f6f6f6',
                      fontWeight: 'bold',
                    }}
                    align={'right'}
                  >
                    Total HUF
                  </TableCell>
                  <TableCell
                    sx={{
                      background: '#f6f6f6',
                      fontWeight: 'bold',
                    }}
                    align={'right'}
                  >
                    Per Person HUF
                  </TableCell>
                  <TableCell
                    sx={{
                      background: '#f6f6f6',
                      fontWeight: 'bold',
                    }}
                    align={'right'}
                  >
                    Total EUR
                  </TableCell>
                  <TableCell
                    sx={{
                      background: '#f6f6f6',
                      fontWeight: 'bold',
                    }}
                    align={'right'}
                  >
                    Per Person EUR
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {calculatedSum.map((row: any) => {
                  return (
                    <>
                      <TableRow>
                        <TableCell>{row.name}</TableCell>

                        <TableCell align={'right'}>
                          {row.net.total_huf}
                        </TableCell>
                        <TableCell align={'right'}>{row.net.pp_huf}</TableCell>
                        <TableCell align={'right'}>
                          {row.net.total_eur}
                        </TableCell>
                        <TableCell align={'right'} sx={{}}>
                          {row.net.pp_eur}
                        </TableCell>

                        <TableCell
                          align={'right'}
                          sx={{
                            background: '#f6f6f6',
                            fontWeight: 'bold',
                          }}
                        >
                          {row.gross.total_huf}
                        </TableCell>
                        <TableCell
                          align={'right'}
                          sx={{
                            background: '#f6f6f6',
                            fontWeight: 'bold',
                          }}
                        >
                          {row.gross.pp_huf}
                        </TableCell>
                        <TableCell
                          align={'right'}
                          sx={{
                            background: '#f6f6f6',
                            fontWeight: 'bold',
                          }}
                        >
                          {row.gross.total_eur}
                        </TableCell>
                        <TableCell
                          align={'right'}
                          sx={{
                            background: '#f6f6f6',
                            fontWeight: 'bold',
                          }}
                        >
                          {row.gross.pp_eur}
                        </TableCell>
                      </TableRow>
                    </>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {openConfirmDialog && (
        <Dialog
          open={openConfirmDialog}
          onClose={() => {
            setOpenConfirmDialog(false);
          }}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
        >
          <DialogTitle id="alert-dialog-title">
            {'Are you sure you want to delete it?'}
          </DialogTitle>
          <DialogActions>
            <MUIButton
              onClick={() => {
                setOpenConfirmDialog(false);
              }}
            >
              Cancel
            </MUIButton>
            <MUIButton
              variant={'contained'}
              color={'error'}
              onClick={() => {
                const deletedRow = openConfirmDialog?.row;
                if (calculatorMode) {
                  const rows = [
                    ...propertiesForCalculate[`${openConfirmDialog?.tab}`],
                  ];
                  const newRows = rows.filter(
                    (row: any) => row.id !== deletedRow.id
                  );
                  setPropertiesForCalculate({
                    ...propertiesForCalculate,
                    [`${openConfirmDialog?.tab}`]: newRows,
                  });

                  setOpenConfirmDialog(false);
                } else {
                  openConfirmDialog?.schema.client
                    .delete(deletedRow.id as number, { id: deletedRow.id })
                    .then((res: any) => {
                      const rows = [...properties[`${openConfirmDialog?.tab}`]];
                      const newRows = rows.filter(
                        (row: any) => row.id !== deletedRow.id
                      );
                      setProperties({
                        ...properties,
                        [`${openConfirmDialog?.tab}`]: newRows,
                      });
                      toaster.show({
                        intent: 'success',
                        message: `Successfully deleted ${schema.name}: ${
                          deletedRow?.name ? deletedRow?.name : deletedRow?.id
                        }`,
                      });
                      setOpenConfirmDialog(false);

                      setQuery('progress');
                      timerRef.current = setTimeout(() => {
                        setQuery('success');
                      }, 2000);
                    })
                    .catch((e: any) => {
                      toaster.show({
                        icon: 'error',
                        message: `Error: Unable to delete ${schema.name}: ${
                          deletedRow?.name ? deletedRow?.name : deletedRow?.id
                        }`,
                        intent: 'danger',
                      });
                    });
                }
              }}
              autoFocus
            >
              Delete
            </MUIButton>
          </DialogActions>
        </Dialog>
      )}

      {openViewPrices?.name && (
        <Dialog
          open={openViewPrices}
          onClose={() => {
            setOpenViewPrices(undefined);
          }}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
          sx={{
            '& .MuiPaper-root.MuiDialog-paper': {
              width: '100%',
              maxWidth: '100%',
            },
          }}
        >
          <DialogTitle id="alert-dialog-title">
            {`${openViewPrices?.name} - Prices`}
          </DialogTitle>

          <DialogContent>
            <TableContainer component={'div'}>
              <Table
                aria-label="simple table"
                size="small"
                sx={{
                  minWidth: 6000,
                  '& .MuiTableRow-root .MuiTableCell-root': {
                    //borderRight: '1px solid'
                  },
                }}
              >
                <TableHead
                  sx={{
                    '& .MuiTableCell-root': {
                      color: 'inherit',
                      padding: '5px',
                    },
                    '& .MuiTableRow-root .MuiTableCell-root.MuiTableCell-head': {
                      border: 'none',
                    },
                    background: '#256861',
                    color: '#fff',
                  }}
                >
                  <TableRow
                    sx={{
                      '& .MuiTableCell-root': {
                        overflowWrap: 'anywhere',
                      },
                    }}
                  >
                    {Object.keys(openViewPriceColumns).map((column: any) => {
                      return (
                        <TableCell
                          sx={{
                            width: 120,
                            textAlign: 'center',
                          }}
                        >
                          {openViewPriceColumns[column]}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow
                    sx={{
                      '& .MuiTableCell-root': {
                        overflowWrap: 'anywhere',
                      },
                    }}
                  >
                    {Object.keys(openViewPriceColumns).map((column: any) => {
                      return (
                        <TableCell
                          sx={{
                            borderRight: '1px solid #e3e3e3',
                            padding: '5px',
                          }}
                        >
                          <SPNumberField
                            disabled={true}
                            onChange={() => {}}
                            defaultValue={
                              openViewPrices[`${column}`]
                                ? openViewPrices[`${column}`]
                                : null
                            }
                            isActive={true}
                          />
                        </TableCell>
                      );
                    })}
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </DialogContent>
        </Dialog>
      )}

      {openedSlidingPane?.row?.id && (
        <SlidingPaneEdit
          demo={calculatorMode}
          schema={openedSlidingPane?.schema}
          id={openedSlidingPane?.row?.id}
          onSubmit={(value) => {
            if (calculatorMode) {
              const tabDetails = [
                ...propertiesForCalculate[`${openedSlidingPane?.tab}`],
              ];
              const newData = tabDetails.map((item: any) => {
                if (
                  openedSlidingPane?.schema.name.toLowerCase() === 'services'
                ) {
                  item.services = item.services.map((serviceItem: any) => {
                    return serviceItem?.id === value?.id ? value : serviceItem;
                  });
                } else {
                  if (item?.id === value?.id) {
                    return value;
                  }
                }
                return item;
              });

              setPropertiesForCalculate({
                ...propertiesForCalculate,
                [`${openedSlidingPane?.tab}`]: newData,
              });
            } else {
              setQuery('progress');
              timerRef.current = setTimeout(() => {
                setQuery('success');
              }, 2000);
              fetchServices();
            }
            setOpenedSlidingPane({});
            return true;
          }}
          propertiesFromTable={
            openedSlidingPane?.row ? openedSlidingPane?.row : {}
          }
          onClose={(value) => {
            setOpenedSlidingPane({});
          }}
        />
      )}

      {currentAddNewPaneEntity?.row?.id && (
        <SlidingPaneAddNew
          schema={currentAddNewPaneEntity?.schema}
          mainSchema={schema}
          id={currentAddNewPaneEntity?.row?.id}
          hasSlidingPane={false}
          onSubmit={(value: any) => {
            fetchServices();
            setCurrentAddNewPaneEntity({
              schema: schema,
              row: {},
              key: undefined,
            });

            setQuery('progress');
            timerRef.current = setTimeout(() => {
              setQuery('success');
            }, 2000);
          }}
          propertiesFromTable={
            currentAddNewPaneEntity?.row ? currentAddNewPaneEntity?.row : {}
          }
          row={currentAddNewPaneEntity?.row ? currentAddNewPaneEntity?.row : {}}
          onClose={(value) => {
            setCurrentAddNewPaneEntity({
              schema: schema,
              row: {},
              key: undefined,
            });
          }}
        />
      )}
    </>
  );
};
